import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage, useIntl } from 'react-intl';

import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalErrorMessages, generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useParking } from 'src/providers/ParkingProvider';
import { useDeleteMembershipCloudUser } from 'src/features/MembershipCloudUsers/api/useDeleteMembershipCloudUser';
import { membershipUsersMessages } from 'src/features/MembershipCloudUsers/utils/messages';
import { LIST_MEMBERSHIP_CLOUD_USERS_RESOURCE } from 'src/features/MembershipCloudUsers/api/useListMembershipCloudUsers';
import { MembershipCloudUser } from 'src/features/membership/types';

interface DeleteMembershipCloudUserProps<TData> extends DataTableNewEntryProps<TData> {
    selectedUser: MembershipCloudUser;
    membershipId: string;
    children?: React.ReactNode;
}

function DeleteMembershipCloudUser<TData>({
    selectedUser,
    membershipId,
    semantics,
    children,
}: DeleteMembershipCloudUserProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const { mutate: deleteUser, isPending: isPendingDelete } = useDeleteMembershipCloudUser({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_MEMBERSHIP_CLOUD_USERS_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(membershipUsersMessages.deleteCloudUserSuccess),
                description: formatMessage(membershipUsersMessages.deleteCloudUserSuccessDescription, {
                    name: selectedUser.cloud_user.name,
                }),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const onSubmit = () => deleteUser({ userId: selectedUser.id, membershipId, parkingId });

    return (
        <Dialog modal open={isModalOpen} onOpenChange={setIsModalOpen}>
            {children && <DialogTrigger asChild>{children}</DialogTrigger>}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={ICON_MAP.DELETE} />
                        <div>
                            <DialogTitle>
                                <FormattedMessage
                                    id={generalMessages.deleteEntry.id}
                                    defaultMessage={generalMessages.deleteEntry.defaultMessage}
                                    values={{ entry: semantics?.entryName?.toLowerCase() }}
                                />
                            </DialogTitle>
                            <DialogDescription>
                                <FormattedMessage
                                    id={membershipUsersMessages.deleteCloudUserSubtitle.id}
                                    defaultMessage={membershipUsersMessages.deleteCloudUserSubtitle.defaultMessage}
                                    values={{
                                        name: selectedUser.cloud_user.name,
                                    }}
                                />
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <p className="text-gray-500">
                        <FormattedMessage
                            id={membershipUsersMessages.confirmDeleteCloudUser.id}
                            defaultMessage={membershipUsersMessages.confirmDeleteCloudUser.defaultMessage}
                            values={{
                                name: selectedUser.cloud_user.name,
                            }}
                        />
                    </p>
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="destructiveLight"
                        disabled={isPendingDelete}
                        type="submit"
                        onClick={onSubmit}
                        className="flex-1"
                    >
                        {isPendingDelete && (
                            <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />
                        )}
                        <FormattedMessage
                            id={generalMessages.deleteEntry.id}
                            defaultMessage={generalMessages.deleteEntry.defaultMessage}
                            values={{ entry: semantics?.entryName?.toLowerCase() }}
                        />
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default DeleteMembershipCloudUser;
